// RH-PAIE-1/RH-PAIE-2C — Onglet "Dossier salarial" de la fiche employé.
// Composant autonome (pattern tableau→cartes mobile, SOGAS_UI_SKILL) importé dans
// EmployeProfilPage.jsx plutôt qu'inliné, pour ne pas alourdir davantage ce fichier déjà volumineux.
import { useState, useEffect, useCallback } from 'react';
import { libelleSite } from '../utils/organisation';
import axios from 'axios';
import CategorieSelector, { chargerCategories } from './CategorieSelector';
import { FAMILLES_PROFESSIONNELLES, humaniserFamilleProfessionnelle } from '../constants/libellesHumains';

// RH-PAIE-2E §9 — à la création du dossier salarial INITIAL, seules ces sources ont un sens
// métier (rien qui présuppose un dossier déjà existant, ex. Avenant/Promotion/Reclassement).
const SOURCES_CREATION_INITIALE = ['CONTRAT', 'REPRISE_EXISTANT', 'DECISION_SOGAS', 'CORRECTION_ADMINISTRATIVE'];

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const ROUGE = '#C62828';
const ORANGE = '#E65100';
const GRIS_BORD = '#E0E0E0';
const GRIS_TEXTE = '#666';

const SOURCES = [
  { v: 'CONTRAT', l: 'Contrat initial' },
  { v: 'AVENANT', l: 'Avenant / augmentation individuelle' },
  { v: 'PROMOTION', l: 'Promotion' },
  { v: 'RECLASSEMENT', l: 'Reclassement' },
  { v: 'DECISION_SOGAS', l: 'Décision SOGAS' },
  { v: 'AUGMENTATION_GENERALE', l: 'Augmentation générale' },
  { v: 'REPRISE_EXISTANT', l: 'Reprise d\'un état existant' },
  { v: 'CORRECTION_ADMINISTRATIVE', l: 'Correction administrative' },
];
const SOURCES_SALAIRE_LIBRE = ['REPRISE_EXISTANT', 'CORRECTION_ADMINISTRATIVE'];
const MODES_PAIEMENT = [
  { v: 'VIREMENT_BANCAIRE', l: 'Virement bancaire' },
  { v: 'CHEQUE', l: 'Chèque' },
  { v: 'ESPECES', l: 'Espèces' },
  { v: 'AUTRE', l: 'Autre' },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '–';
const fmtMontant = (v) => (v === null || v === undefined || v === '') ? '–' : Number(v).toLocaleString('fr-FR') + ' FCFA';
// RH-PAIE-2F §8 — une catégorie seule est ambiguë (le même code peut exister dans plusieurs
// familles) : toujours afficher la famille professionnelle avec elle, ou "À classer" si absente.
const fmtCategorie = (v) => {
  if (!v?.categorie) return '–';
  return `${v.famille_professionnelle ? humaniserFamilleProfessionnelle(v.famille_professionnelle) : 'Famille professionnelle : à classer'} — Catégorie ${v.categorie}`;
};

const STATUT_LABELS = { a_valider: ['En attente de validation', ORANGE], validee: ['Validée', VERT], annulee: ['Annulée', GRIS_TEXTE] };

function Badge({ texte, couleur }) {
  return <span style={{ background: couleur + '1a', color: couleur, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{texte}</span>;
}

function Modal({ titre, onClose, children, large }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: large ? 620 : 440, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, color: VERT, fontSize: 15 }}>{titre}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmModal({ action, onClose }) {
  if (!action) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 380, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 15, color: '#1a1a1a' }}>{action.titre}</h3>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#555', lineHeight: 1.5 }}>{action.message}</p>
        {action.champMotif && (
          <textarea placeholder="Motif (obligatoire)" value={action.motif} onChange={(e) => action.setMotif(e.target.value)}
            style={{ width: '100%', minHeight: 70, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 16, fontFamily: 'inherit' }} />
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', color: '#555', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
          <button
            disabled={action.champMotif && !action.motif?.trim()}
            onClick={() => action.onConfirm()}
            style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: action.couleurBtn || VERT, color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600, opacity: (action.champMotif && !action.motif?.trim()) ? 0.5 : 1 }}
          >{action.btnLabel || 'Confirmer'}</button>
        </div>
      </div>
    </div>
  );
}

const ALERTES_QUALITE = {
  INCOMPLET: { texte: 'Dossier incomplet — salaire et/ou catégorie à renseigner.', couleur: ROUGE },
  A_VALIDER: { texte: 'Une version salariale est en attente de validation.', couleur: ORANGE },
  PROGRAMME: { texte: 'Une version future est programmée, aucune version active actuellement.', couleur: ORANGE },
  SANS_PROFIL: { texte: 'Aucun dossier salarial n\'existe encore pour cet employé.', couleur: ROUGE },
  COMPLET: null,
};

export default function DossierSalarialTab({ employeId, userConnecte, employe }) {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [modalOuvert, setModalOuvert] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMotif, setConfirmMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [grille, setGrille] = useState({ loading: false, resolue: null, erreur: '' });
  const [categories, setCategories] = useState([]);

  useEffect(() => { chargerCategories().then(setCategories); }, []);

  const perms = userConnecte?.permissions_effectives || {};
  const ROLES_LECTURE = ['drh', 'rh_principal', 'assistant_rh'];
  const ROLES_MODIFICATION = ['drh', 'rh_principal'];
  const ROLES_VALIDATION = ['drh'];
  const peutLire = ROLES_LECTURE.includes(userConnecte?.role) || perms.peut_voir_dossier_salarial === true;
  const peutModifier = ROLES_MODIFICATION.includes(userConnecte?.role) || perms.peut_modifier_dossier_salarial === true;
  const peutValider = ROLES_VALIDATION.includes(userConnecte?.role) || perms.peut_valider_dossier_salarial === true;

  const charger = useCallback(async () => {
    setLoading(true); setErreur('');
    try {
      const { data } = await axios.get(`${API}/employes/${employeId}/dossier-salarial`);
      setDossier(data.data);
    } catch (e) {
      setErreur(e.response?.data?.message || 'Impossible de charger le dossier salarial.');
    } finally { setLoading(false); }
  }, [employeId]);

  useEffect(() => { if (peutLire) charger(); else setLoading(false); }, [charger, peutLire]);

  // RH-PAIE-2C §13 — résolution live du salaire catégoriel dès que catégorie/échelon/date
  // d'effet changent, pour un affichage en lecture seule (jamais une saisie libre).
  const salaireLibre = SOURCES_SALAIRE_LIBRE.includes(form.source);
  useEffect(() => {
    if (!modalOuvert || salaireLibre || !form.famille_professionnelle || !form.categorie || !form.date_effet) {
      setGrille({ loading: false, resolue: null, erreur: '' });
      return;
    }
    let annule = false;
    setGrille({ loading: true, resolue: null, erreur: '' });
    axios.get(`${API}/paie/parametres/SALAIRE_CATEGORIEL_BASE/resoudre`, {
      params: { date_reference: form.date_effet, famille_professionnelle: form.famille_professionnelle, categorie: form.categorie, echelon: form.echelon || undefined },
    }).then(({ data }) => {
      if (annule) return;
      if (data.data) setGrille({ loading: false, resolue: data.data, erreur: '' });
      else setGrille({ loading: false, resolue: null, erreur: 'Aucun salaire catégoriel validé n\'est disponible pour cette famille, cette catégorie, cet échelon et cette date d\'effet. Configurez ou validez d\'abord la grille salariale dans les paramètres.' });
    }).catch((e) => {
      if (annule) return;
      setGrille({ loading: false, resolue: null, erreur: e.response?.data?.message || 'Impossible de résoudre le salaire catégoriel.' });
    });
    return () => { annule = true; };
  }, [modalOuvert, salaireLibre, form.famille_professionnelle, form.categorie, form.echelon, form.date_effet]);

  if (!peutLire) {
    return <div style={{ padding: 20, color: GRIS_TEXTE, fontSize: 13 }}>Vous n'avez pas accès au dossier salarial de cet employé.</div>;
  }
  if (loading) return <div style={{ padding: 20, color: GRIS_TEXTE, fontSize: 13 }}>Chargement…</div>;
  if (erreur && !modalOuvert) return <div style={{ padding: 20, color: ROUGE, fontSize: 13 }}>{erreur}</div>;

  const { applicable, historique, statutQualite } = dossier || {};
  const alerte = ALERTES_QUALITE[statutQualite];
  const aDossierInitial = (historique || []).length > 0;

  const ouvrirNouvelleVersion = () => {
    setForm({
      date_effet: new Date().toISOString().slice(0, 10),
      motif: '', source: '',
      type_remuneration: applicable?.type_remuneration || '',
      famille_professionnelle: applicable?.famille_professionnelle || '',
      categorie: applicable?.categorie || '',
      echelon: applicable?.echelon || '',
      classification: applicable?.classification || '',
      statut_cadre: applicable?.statut_cadre || '',
      salaire_base: applicable?.salaire_base || '',
      sursalaire: applicable?.sursalaire || '',
      mode_paiement: applicable?.mode_paiement || '',
      mode_paiement_detail: applicable?.mode_paiement_detail || '',
    });
    setModalOuvert(true);
  };

  const categorieSelectionnee = categories.find((c) => c.code === form.categorie && c.famille_professionnelle === form.famille_professionnelle);
  const echelonsDisponibles = categorieSelectionnee?.echelons_disponibles || [];
  const salaireBaseEffectif = salaireLibre ? form.salaire_base : (grille.resolue?.valeur_numerique ?? '');
  const formValide = form.date_effet && form.motif?.trim() && form.source && form.type_remuneration
    && form.famille_professionnelle && form.categorie
    && (salaireLibre || (!grille.loading && !grille.erreur));

  const soumettreVersion = async () => {
    setSaving(true);
    try {
      const payload = { ...form, salaire_base: salaireBaseEffectif };
      ['salaire_base', 'sursalaire'].forEach((f) => { if (payload[f] === '') payload[f] = null; });
      ['type_remuneration', 'categorie', 'echelon', 'classification', 'statut_cadre', 'mode_paiement', 'mode_paiement_detail'].forEach((f) => { if (payload[f] === '') payload[f] = null; });
      await axios.post(`${API}/employes/${employeId}/dossier-salarial/versions`, payload);
      setModalOuvert(false);
      await charger();
    } catch (e) {
      alert2(e.response?.data?.message || 'Erreur lors de la création de la version.');
    } finally { setSaving(false); }
  };

  // Pas de window.alert (SOGAS_UI_SKILL) — bandeau d'erreur simple réutilisant l'état erreur.
  function alert2(msg) { setErreur(msg); setTimeout(() => setErreur(''), 6000); }

  const demanderValidation = (version) => {
    setConfirmAction({
      titre: 'Valider cette version salariale',
      message: `Confirmer la validation de la version n°${version.version} (effet au ${fmtDate(version.date_effet)}). Cette action synchronisera le dossier si la version est actuellement applicable.`,
      btnLabel: 'Valider', couleurBtn: VERT,
      onConfirm: async () => {
        try {
          await axios.patch(`${API}/employes/${employeId}/dossier-salarial/versions/${version.id}/valider`);
          setConfirmAction(null); await charger();
        } catch (e) { setConfirmAction(null); alert2(e.response?.data?.message || 'Erreur lors de la validation.'); }
      },
    });
  };

  const executerAnnulation = async (version) => {
    try {
      await axios.patch(`${API}/employes/${employeId}/dossier-salarial/versions/${version.id}/annuler`, { motif: confirmMotif });
      setConfirmAction(null); setConfirmMotif(''); await charger();
    } catch (e) { alert2(e.response?.data?.message || 'Erreur lors de l\'annulation.'); }
  };

  return (
    <div>
      <style>{`
        .dsv-wrap { overflow-x: auto; width: 100%; max-width: 100%; box-sizing: border-box; }
        .dsv-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        .dsv-table th { text-align: left; padding: 8px 10px; color: #888; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 2px solid ${GRIS_BORD}; }
        .dsv-table td { padding: 9px 10px; border-bottom: 1px solid #f0f0f0; }
        .dsv-cards { display: none; }
        @media (max-width: 767px) {
          .dsv-wrap { overflow-x: visible; }
          .dsv-table { display: none; }
          .dsv-cards { display: block; }
        }
      `}</style>

      <div style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 12, padding: '14px 20px', marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>Poste de référence</div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: employe?.poste ? '#222' : GRIS_TEXTE }}>
          {employe?.poste || 'Poste non renseigné'}
        </div>
        <div style={{ fontSize: 12, color: GRIS_TEXTE, marginTop: 3 }}>
          {[employe?.site && libelleSite(employe.site), employe?.departement, employe?.service].filter(Boolean).join(' · ') || '–'}
        </div>
      </div>

      {alerte && (
        <div style={{ background: alerte.couleur + '12', border: `1px solid ${alerte.couleur}44`, color: alerte.couleur, borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
          {alerte.texte}
        </div>
      )}
      {erreur && modalOuvert === false && (
        <div style={{ background: ROUGE + '12', border: `1px solid ${ROUGE}44`, color: ROUGE, borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
          {erreur}
        </div>
      )}

      <div style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h4 style={{ margin: 0, fontSize: 14, color: VERT }}>Synthèse actuelle</h4>
          {peutModifier && (
            <button onClick={ouvrirNouvelleVersion} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {aDossierInitial ? 'Nouvelle version' : 'Créer le dossier salarial'}
            </button>
          )}
        </div>
        {applicable ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
            <ChampSynthese label="Type de rémunération" valeur={applicable.type_remuneration || '–'} />
            <ChampSynthese label="Catégorie" valeur={fmtCategorie(applicable)} />
            <ChampSynthese label="Échelon" valeur={applicable.echelon || 'Non applicable'} />
            <ChampSynthese label="Statut cadre" valeur={applicable.statut_cadre === 'cadre' ? 'Cadre' : applicable.statut_cadre === 'non_cadre' ? 'Non-cadre' : '–'} />
            <ChampSynthese label="Salaire de base" valeur={fmtMontant(applicable.salaire_base)} />
            <ChampSynthese label="Sursalaire" valeur={fmtMontant(applicable.sursalaire)} />
            <ChampSynthese label="Date d'effet" valeur={fmtDate(applicable.date_effet)} />
            <ChampSynthese label="Source" valeur={applicable.source} />
          </div>
        ) : (
          <div style={{ color: GRIS_TEXTE, fontSize: 13 }}>
            {aDossierInitial ? 'Aucune version salariale active à ce jour.' : 'Aucun dossier salarial n\'existe encore pour cet employé — aucune catégorie ni aucun salaire n\'est jamais rempli automatiquement.'}
          </div>
        )}
      </div>

      {/* RH-PAIE-2I §7 — la version en attente ne doit jamais être présentée comme déjà
          applicable : bloc séparé, comparaison explicite avec la synthèse actuelle, jamais
          fusionné avec elle. */}
      {historique?.some((v) => v.statut === 'a_valider') && (
        <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, color: '#E65100' }}>Version en attente de validation</h4>
          <p style={{ fontSize: 11.5, color: GRIS_TEXTE, marginTop: 0, marginBottom: 14 }}>
            Proposée mais pas encore applicable — elle ne remplacera la synthèse actuelle qu'après validation par un tiers autorisé.
          </p>
          {historique.filter((v) => v.statut === 'a_valider').map((v) => {
            const totalActuel = (Number(applicable?.salaire_base) || 0) + (Number(applicable?.sursalaire) || 0);
            const totalPropose = (Number(v.salaire_base) || 0) + (Number(v.sursalaire) || 0);
            const nAutreValidateurAttente = v.auteur_id !== userConnecte?.id;
            return (
              <div key={v.id} style={{ background: '#fff', border: '1px solid #FFE082', borderRadius: 8, padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, fontSize: 12.5, marginBottom: 10 }}>
                  <ChampSynthese label="Catégorie validée" valeur={fmtCategorie(applicable)} />
                  <ChampSynthese label="Catégorie proposée" valeur={fmtCategorie(v)} />
                  <ChampSynthese label="Salaire de base validé" valeur={fmtMontant(applicable?.salaire_base)} />
                  <ChampSynthese label="Salaire de base proposé" valeur={fmtMontant(v.salaire_base)} />
                  <ChampSynthese label="Sursalaire validé" valeur={fmtMontant(applicable?.sursalaire)} />
                  <ChampSynthese label="Sursalaire proposé" valeur={fmtMontant(v.sursalaire)} />
                  <ChampSynthese label="Total mensuel validé" valeur={fmtMontant(totalActuel)} />
                  <ChampSynthese label="Total mensuel proposé" valeur={fmtMontant(totalPropose)} />
                  <ChampSynthese label="Date d'effet" valeur={fmtDate(v.date_effet)} />
                  <ChampSynthese label="Auteur" valeur={v.auteur_prenom ? `${v.auteur_prenom} ${v.auteur_nom}` : '–'} />
                </div>
                <div style={{ fontSize: 11.5, color: GRIS_TEXTE, marginBottom: peutValider ? 10 : 0 }}>
                  {peutValider
                    ? (nAutreValidateurAttente ? 'Vous disposez de la permission de validation pour cette version.' : 'Vous en êtes l\'auteur — la politique de séparation auteur/validateur en vigueur peut refuser que vous la validiez vous-même.')
                    : 'Validation requise : un utilisateur disposant de la permission de validation du dossier salarial.'}
                </div>
                {peutValider && nAutreValidateurAttente && (
                  <button onClick={() => demanderValidation(v)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Valider cette version</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 12, padding: '18px 20px' }}>
        <h4 style={{ margin: '0 0 14px', fontSize: 14, color: VERT }}>Historique des versions</h4>
        {(!historique || historique.length === 0) ? (
          <div style={{ color: GRIS_TEXTE, fontSize: 13 }}>Aucune version enregistrée.</div>
        ) : (
          <div className="dsv-wrap">
            <table className="dsv-table">
              <thead>
                <tr>
                  <th>Période</th><th>Catégorie</th><th>Salaire</th><th>Sursalaire</th><th>Motif / Source</th><th>Statut</th><th>Auteur</th><th>Validateur</th>{peutValider && <th></th>}
                </tr>
              </thead>
              <tbody>
                {historique.map((v) => {
                  const [libelle, couleur] = STATUT_LABELS[v.statut] || [v.statut, GRIS_TEXTE];
                  const estEnAttente = v.statut === 'a_valider';
                  const nAutreValidateur = v.auteur_id !== userConnecte?.id;
                  return (
                    <tr key={v.id}>
                      <td>{fmtDate(v.date_effet)}{v.date_fin ? ` → ${fmtDate(v.date_fin)}` : ''}</td>
                      <td>{fmtCategorie(v)}</td>
                      <td>{fmtMontant(v.salaire_base)}</td>
                      <td>{fmtMontant(v.sursalaire)}</td>
                      <td style={{ maxWidth: 220 }}>{v.source} — <span style={{ color: GRIS_TEXTE }}>{v.motif}</span></td>
                      <td><Badge texte={libelle} couleur={couleur} /></td>
                      <td>{v.auteur_prenom ? `${v.auteur_prenom} ${v.auteur_nom}` : '–'}</td>
                      <td>{v.validateur_prenom ? `${v.validateur_prenom} ${v.validateur_nom}` : '–'}</td>
                      {peutValider && (
                        <td>
                          {estEnAttente && nAutreValidateur && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => demanderValidation(v)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Valider</button>
                              <button onClick={() => { setConfirmMotif(''); setConfirmAction({ version: v, titre: 'Annuler cette version', message: `La version n°${v.version} sera marquée annulée (motif obligatoire).`, champMotif: true }); }} style={{ background: '#fff', color: ROUGE, border: `1px solid ${ROUGE}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
                            </div>
                          )}
                          {estEnAttente && !nAutreValidateur && (
                            <span style={{ fontSize: 10.5, color: GRIS_TEXTE }}>Auteur — validation par un tiers requise</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="dsv-cards">
              {historique.map((v) => {
                const [libelle, couleur] = STATUT_LABELS[v.statut] || [v.statut, GRIS_TEXTE];
                return (
                  <div key={v.id} style={{ border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong style={{ fontSize: 13 }}>{fmtDate(v.date_effet)}</strong>
                      <Badge texte={libelle} couleur={couleur} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, marginBottom: 6 }}>
                      <div><div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>Catégorie</div>{fmtCategorie(v)}</div>
                      <div><div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>Salaire</div>{fmtMontant(v.salaire_base)}</div>
                    </div>
                    <div style={{ fontSize: 11.5, color: GRIS_TEXTE }}>{v.source} — {v.motif}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {modalOuvert && (
        <Modal titre={aDossierInitial ? 'Nouvelle version salariale' : 'Créer le dossier salarial initial'} onClose={() => setModalOuvert(false)} large>
          {erreur && <div style={{ background: ROUGE + '12', color: ROUGE, borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 12 }}>{erreur}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <ChampForm label="Date d'effet *" type="date" value={form.date_effet} onChange={(v) => setForm({ ...form, date_effet: v })} />
            <ChampSelect label="Source *" value={form.source} onChange={(v) => setForm({ ...form, source: v })}
              options={[{ v: '', l: 'Sélectionner une source' }, ...(aDossierInitial ? SOURCES : SOURCES.filter((s) => SOURCES_CREATION_INITIALE.includes(s.v)))]} />
            <ChampSelect label="Type de rémunération *" value={form.type_remuneration} onChange={(v) => setForm({ ...form, type_remuneration: v })}
              options={[{ v: '', l: 'Sélectionner…' }, { v: 'mensuel', l: 'Mensuel' }, { v: 'horaire', l: 'Horaire' }, { v: 'journalier', l: 'Journalier' }]} />
            <ChampSelect label="Famille professionnelle *" value={form.famille_professionnelle} onChange={(v) => setForm({ ...form, famille_professionnelle: v, categorie: '', echelon: '' })}
              options={[{ v: '', l: 'Sélectionner une famille' }, ...Object.entries(FAMILLES_PROFESSIONNELLES).map(([v, l]) => ({ v, l }))]} />
            <CategorieSelector label="Catégorie *" value={form.categorie} onChange={(v) => setForm({ ...form, categorie: v, echelon: '' })} allowEmpty={false} disabled={!form.famille_professionnelle} familleProfessionnelle={form.famille_professionnelle || undefined} />
            <ChampSelect label="Échelon" value={form.echelon} onChange={(v) => setForm({ ...form, echelon: v })}
              options={echelonsDisponibles.length ? [{ v: '', l: 'Non applicable' }, ...echelonsDisponibles.map((e) => ({ v: e, l: e }))] : [{ v: '', l: 'Non applicable' }]}
              disabled={echelonsDisponibles.length === 0} />
            <ChampSelect label="Statut cadre" value={form.statut_cadre} onChange={(v) => setForm({ ...form, statut_cadre: v })} options={[{ v: '', l: '—' }, { v: 'cadre', l: 'Cadre' }, { v: 'non_cadre', l: 'Non-cadre' }]} />

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Salaire de base (FCFA)</label>
              {salaireLibre ? (
                <input type="number" value={form.salaire_base ?? ''} onChange={(e) => setForm({ ...form, salaire_base: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              ) : (
                <div style={{ padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 13, background: '#f7f7f7', minHeight: 37, boxSizing: 'border-box' }}>
                  {grille.loading ? 'Résolution…' : grille.resolue ? (
                    <span style={{ fontWeight: 700, color: VERT }}>{fmtMontant(grille.resolue.valeur_numerique)}</span>
                  ) : form.categorie ? <span style={{ color: ROUGE, fontSize: 12 }}>Aucun salaire catégoriel validé</span> : <span style={{ color: '#999' }}>Sélectionnez une catégorie</span>}
                </div>
              )}
              {!salaireLibre && grille.resolue && (
                <div style={{ fontSize: 11, color: GRIS_TEXTE, marginTop: 3 }}>
                  Grille version {grille.resolue.version} · applicable depuis le {fmtDate(grille.resolue.date_debut)} · source {grille.resolue.source}
                </div>
              )}
              {!salaireLibre && !grille.loading && grille.erreur && (
                <div style={{ fontSize: 11.5, color: ROUGE, marginTop: 3 }}>{grille.erreur}</div>
              )}
            </div>

            <ChampForm label="Sursalaire (FCFA)" type="number" value={form.sursalaire} onChange={(v) => setForm({ ...form, sursalaire: v })} />
            <ChampSelect label="Mode de paiement" value={form.mode_paiement} onChange={(v) => setForm({ ...form, mode_paiement: v })}
              options={[{ v: '', l: '—' }, ...MODES_PAIEMENT]} />
            {form.mode_paiement === 'AUTRE' && (
              <ChampForm label="Détail du mode de paiement *" value={form.mode_paiement_detail} onChange={(v) => setForm({ ...form, mode_paiement_detail: v })} />
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif *</label>
            <textarea value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })}
              style={{ width: '100%', minHeight: 70, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <button onClick={() => setModalOuvert(false)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', color: '#555', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
            <button disabled={saving || !formValide} onClick={soumettreVersion}
              style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600, opacity: (saving || !formValide) ? 0.5 : 1 }}>
              {saving ? 'Enregistrement…' : (aDossierInitial ? 'Créer la version' : 'Créer le dossier salarial')}
            </button>
          </div>
        </Modal>
      )}

      {confirmAction && confirmAction.version && (
        <ConfirmModal
          action={{
            titre: confirmAction.titre, message: confirmAction.message, champMotif: true,
            motif: confirmMotif, setMotif: setConfirmMotif,
            btnLabel: 'Confirmer l\'annulation', couleurBtn: ROUGE,
            onConfirm: () => executerAnnulation(confirmAction.version),
          }}
          onClose={() => { setConfirmAction(null); setConfirmMotif(''); }}
        />
      )}
      {confirmAction && !confirmAction.version && (
        <ConfirmModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      )}
    </div>
  );
}

function ChampSynthese({ label, valeur }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{valeur}</div>
    </div>
  );
}

function ChampForm({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>{label}</label>
      <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
    </div>
  );
}

function ChampSelect({ label, value, onChange, options, disabled = false }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>{label}</label>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: disabled ? '#f5f5f5' : '#fff' }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
